import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { markSmartlinkIframeNavigation } from '../../lib/smartlink';
import { useGlobal } from '../../context/GlobalContext';
import styles from './Hero.module.css';

const HOME_DEEPLINK_STORAGE_KEY = 'koppiv4:home-deeplink';

const normalizeDeeplinkSlug = (raw) =>
  (raw || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+/, '')
    .replace(/^\?+/, '')
    .replace(/=.*$/, '');

const getPrimaryQuerySlug = (search = '') => normalizeDeeplinkSlug(
  String(search || '')
    .replace(/^\?+/, '')
    .split('&')[0]
);

const getLocalizedSearch = (search, nextSlug) => {
  const cleanSlug = String(nextSlug || '').trim().replace(/^\/+/, '').replace(/^\?+/, '');
  if (!cleanSlug) return search || '';

  const parts = String(search || '').replace(/^\?+/, '').split('&').filter(Boolean);
  const firstPart = parts[0] || '';
  const valuePart = firstPart.includes('=') ? firstPart.replace(/^[^=]*/, '') : '';
  const rest = parts.slice(1);

  return `?${[`${cleanSlug}${valuePart}`, ...rest].join('&')}`;
};

const Hero = ({ data, secciones }) => {
  if (!data || data.length === 0) return null;

  const heroData = data[0];
  const componentType = heroData.__component;

  switch (componentType) {
    case 'blocks.hero-image':
      return <HeroImage data={heroData} />;
    case 'blocks.hero-video':
      return <HeroVideo data={heroData} />;
    case 'blocks.hero-gallery':
      return <HeroGallery data={heroData} />;
    case 'blocks.hero-iframe360':
      return <HeroIframe360 data={heroData} secciones={secciones} />;
    default:
      return null;
  }
};

const HeroImage = ({ data }) => {
  const imageMedia = getStrapiMediaAttributes(data.image || data.imageUrl, {
    altFallback: data.title || data.subtitle || '',
    titleFallback: data.title || data.subtitle || '',
  });
  const showOverlay = data.showTitle && (data.title || data.subtitle);
  const imageLabel = !data.showTitle ? imageMedia.alt : '';

  const height = data.height || '85dvh';
  const heightMobile = data.heightMobile || height;
  const landscapeHeight = data.landscapeMatchesDesktop !== false ? height : heightMobile;

  return (
    <section
      className={`${styles.hero} ${styles['hero--image']}`}
      title={imageMedia.title}
      role={imageLabel ? 'img' : undefined}
      aria-label={imageLabel || undefined}
      style={{
        backgroundImage: imageMedia.src ? `url(${imageMedia.src})` : undefined,
        '--hero-height': height,
        '--hero-height-mobile': heightMobile,
        '--hero-landscape-height': landscapeHeight,
      }}
    >
      {showOverlay && (
        <div
          className={styles.hero__overlay}
          style={{ opacity: (data.overlayOpacity ?? 30) / 100 }}
        />
      )}
      {data.showTitle && (
        <div className={styles.hero__content}>
          {data.title && <h1 className={styles.hero__title}>{data.title}</h1>}
          {data.subtitle && <p className={styles.hero__subtitle}>{data.subtitle}</p>}
        </div>
      )}
    </section>
  );
};

const HeroVideo = ({ data }) => {
  const videoMedia = getStrapiMediaAttributes(data.video || data.videoUrl, {
    altFallback: data.title || data.subtitle || '',
    titleFallback: data.title || data.subtitle || '',
  });
  const posterMedia = getStrapiMediaAttributes(data.posterImage, {
    altFallback: data.title || data.subtitle || '',
    titleFallback: data.title || data.subtitle || '',
  });
  const showOverlay = data.showTitle && (data.title || data.subtitle);
  const videoLabel = !data.showTitle ? posterMedia.alt || videoMedia.alt : '';
  const videoTitle = posterMedia.title || videoMedia.title;

  const height = data.height || '85dvh';
  const heightMobile = data.heightMobile || height;
  const landscapeHeight = data.landscapeMatchesDesktop !== false ? height : heightMobile;

  return (
    <section
      className={`${styles.hero} ${styles['hero--video']}`}
      title={videoTitle}
      style={{
        '--hero-height': height,
        '--hero-height-mobile': heightMobile,
        '--hero-landscape-height': landscapeHeight,
      }}
    >
      <video
        className={styles.hero__video}
        src={videoMedia.src}
        poster={posterMedia.src}
        autoPlay={data.autoplay !== false}
        loop={data.loop !== false}
        muted={data.muted !== false}
        playsInline
        title={videoTitle}
        aria-label={videoLabel || undefined}
      />
      {showOverlay && (
        <div
          className={styles.hero__overlay}
          style={{ opacity: (data.overlayOpacity ?? 30) / 100 }}
        />
      )}
      {data.showTitle && (
        <div className={styles.hero__content}>
          {data.title && <h1 className={styles.hero__title}>{data.title}</h1>}
          {data.subtitle && <p className={styles.hero__subtitle}>{data.subtitle}</p>}
        </div>
      )}
    </section>
  );
};

const HeroGallery = ({ data }) => {
  const firstImage = data.images?.[0];
  const imageMedia = getStrapiMediaAttributes(firstImage, {
    altFallback: data.title || data.subtitle || '',
    titleFallback: data.title || data.subtitle || '',
  });
  const showOverlay = data.showTitle && (data.title || data.subtitle);
  const imageLabel = !data.showTitle ? imageMedia.alt : '';

  const height = data.height || '85dvh';
  const heightMobile = data.heightMobile || height;
  const landscapeHeight = data.landscapeMatchesDesktop !== false ? height : heightMobile;

  return (
    <section
      className={`${styles.hero} ${styles['hero--gallery']}`}
      title={imageMedia.title}
      role={imageLabel ? 'img' : undefined}
      aria-label={imageLabel || undefined}
      style={{
        backgroundImage: imageMedia.src ? `url(${imageMedia.src})` : undefined,
        '--hero-height': height,
        '--hero-height-mobile': heightMobile,
        '--hero-landscape-height': landscapeHeight,
      }}
    >
      {showOverlay && (
        <div
          className={styles.hero__overlay}
          style={{ opacity: (data.overlayOpacity ?? 30) / 100 }}
        />
      )}
      {data.showTitle && (
        <div className={styles.hero__content}>
          {data.title && <h1 className={styles.hero__title}>{data.title}</h1>}
          {data.subtitle && <p className={styles.hero__subtitle}>{data.subtitle}</p>}
        </div>
      )}
    </section>
  );
};

const HeroIframe360 = ({ data, secciones }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setHeaderHidden, header, headerHidden, smartlink } = useGlobal();
  const iframeRef = useRef(null);
  const sectionRef = useRef(null);
  const currentPath = location.pathname.replace('/', '') || 'home';

  const getIframeUrl = () => {
    // 1) Section links matched by query string (e.g. `/?amenidades`).
    //    Pathname stays `/` so React Router always renders the home; the
    //    `?seccion` selector picks the matching iframe without extra requests.
    //    Secciones come from the standalone `blocks.hero-secciones` block,
    //    extracted in DynamicPage and passed down as a prop.
    if (Array.isArray(secciones) && secciones.length > 0) {
      const search = getPrimaryQuerySlug(location.search);

      if (search) {
        const seccion = secciones.find(
          (s) => normalizeDeeplinkSlug(s?.slug) === search
        );
        if (seccion?.iframeURL) return seccion.iframeURL;
      }
    }

    // 2) Legacy deepLinks by pathname (separate dynamic routes).
    if (Array.isArray(data.deepLinks) && data.deepLinks.length > 0) {
      const deepLink = data.deepLinks.find(
        (link) => link.label?.toLowerCase() === currentPath.toLowerCase()
      );
      if (deepLink?.deepLinkURL) return deepLink.deepLinkURL;
    }

    return data.src;
  };

  useEffect(() => {
    if (!Array.isArray(secciones) || secciones.length === 0 || !location.search) return;

    const searchSlug = getPrimaryQuerySlug(location.search);
    if (!searchSlug) return;

    const currentIndex = secciones.findIndex(
      (seccion) => normalizeDeeplinkSlug(seccion?.slug) === searchSlug
    );

    if (currentIndex >= 0) {
      window.sessionStorage.setItem(
        HOME_DEEPLINK_STORAGE_KEY,
        JSON.stringify({ index: currentIndex, slug: searchSlug })
      );
      return;
    }

    let stored = null;
    try {
      stored = JSON.parse(window.sessionStorage.getItem(HOME_DEEPLINK_STORAGE_KEY) || 'null');
    } catch {
      stored = null;
    }

    const storedIndex = Number(stored?.index);
    const shouldLocalizeFromStoredIndex = Number.isInteger(storedIndex)
      && storedIndex >= 0
      && storedIndex < secciones.length
      && normalizeDeeplinkSlug(stored?.slug) === searchSlug;

    if (!shouldLocalizeFromStoredIndex) return;

    const localizedSlug = normalizeDeeplinkSlug(secciones[storedIndex]?.slug);
    if (!localizedSlug || localizedSlug === searchSlug) return;

    const nextSearch = getLocalizedSearch(location.search, localizedSlug);
    navigate(`${location.pathname || '/'}${nextSearch}${location.hash || ''}`, { replace: true });
  }, [secciones, location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    const shouldHideHeader = header?.isSticky && header?.hideOnIframeClick !== false;

    // Desktop: blur event when iframe steals focus
    const handleWindowBlur = () => {
      if (document.activeElement === iframeRef.current) {
        if (shouldHideHeader) setHeaderHidden(true);
        if (smartlink) markSmartlinkIframeNavigation();
      }
    };

    // Mobile: touchstart on the hero section hides the header
    const sectionEl = sectionRef.current;
    const handleTouchStart = () => {
      if (shouldHideHeader) setHeaderHidden(true);
      if (smartlink) markSmartlinkIframeNavigation();
    };

    window.addEventListener('blur', handleWindowBlur);
    if (sectionEl) {
      sectionEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    }

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      if (sectionEl) {
        sectionEl.removeEventListener('touchstart', handleTouchStart);
      }
    };
  }, [setHeaderHidden, header?.isSticky, header?.hideOnIframeClick, smartlink]);

  const iframeHeight = header?.isSticky && headerHidden ? '90dvh' : '85dvh';

  return (
    <section
      ref={sectionRef}
      className={`${styles.hero} ${styles['hero--iframe360']}${headerHidden ? ` ${styles['hero--expanded']}` : ''}`}
      style={{ height: iframeHeight }}
    >
      <iframe
        ref={iframeRef}
        className={styles.hero__iframe}
        src={getIframeUrl()}
        title={data.title || undefined}
        allowFullScreen
        allow="fullscreen; xr-spatial-tracking"
        loading="lazy"
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation allow-top-navigation-by-user-activation allow-popups-to-escape-sandbox allow-downloads"
        data-hj-allow-iframe="true"
      />
      {data.showTitle && data.title && (
        <div className={`${styles.hero__content} ${styles['hero__content--iframe']}`}>
          <h1 className={styles.hero__title}>{data.title}</h1>
        </div>
      )}
    </section>
  );
};

export default Hero;
